import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { bansApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { formatDateTime } from '@/lib/utils';

export function BansPage() {
  const queryClient = useQueryClient();
  const [showActive, setShowActive] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['bans', { active: showActive }],
    queryFn: () => bansApi.list({ active: showActive }),
  });

  const liftBanMutation = useMutation({
    mutationFn: (id: number) => bansApi.update(id, { active: false }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bans'] }),
  });

  const createBanMutation = useMutation({
    mutationFn: (data: { telegram_user_id: string; reason?: string; duration_days?: number }) =>
      bansApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bans'] });
      setShowCreateModal(false);
    },
  });

  return (
    <Layout title="Баны" subtitle={`Всего: ${data?.total ?? 0}`}>
      {/* Filters */}
      <div className="cyber-card p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              className={`cyber-button text-sm ${showActive ? 'cyber-button-primary' : ''}`}
              onClick={() => setShowActive(true)}
            >
              Активные
            </button>
            <button
              className={`cyber-button text-sm ${!showActive ? 'cyber-button-primary' : ''}`}
              onClick={() => setShowActive(false)}
            >
              Все
            </button>
          </div>
          <button
            className="cyber-button-primary flex items-center gap-2 text-sm"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="w-4 h-4" />
            Добавить бан
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="cyber-card overflow-hidden">
          <table className="cyber-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>Причина</th>
                <th>Тип</th>
                <th>Статус</th>
                <th>Создан</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data?.bans.map((ban) => (
                <tr key={ban.id}>
                  <td className="font-mono text-neon-cyan">#{ban.id}</td>
                  <td className="font-mono">{ban.user_id}</td>
                  <td className="max-w-xs text-gray-300">{ban.reason ?? '—'}</td>
                  <td>
                    {ban.permanent ? (
                      <span className="text-neon-red">Перманентный</span>
                    ) : (
                      <span className="text-neon-yellow">Временный</span>
                    )}
                  </td>
                  <td>
                    {ban.active ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-neon-red/10 text-neon-red border border-neon-red/30">
                        Активен
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-500/10 text-gray-400 border border-gray-500/30">
                        Снят
                      </span>
                    )}
                  </td>
                  <td className="text-gray-400 text-sm">{formatDateTime(ban.created_at)}</td>
                  <td>
                    {ban.active === 1 && (
                      <button
                        className="cyber-button text-sm"
                        onClick={() => liftBanMutation.mutate(ban.id)}
                        disabled={liftBanMutation.isPending}
                      >
                        Снять бан
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!data?.bans || data.bans.length === 0) && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    Баны не найдены
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Ban Modal */}
      {showCreateModal && (
        <CreateBanModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createBanMutation.mutate(data)}
          isSubmitting={createBanMutation.isPending}
        />
      )}
    </Layout>
  );
}

interface CreateBanModalProps {
  onClose: () => void;
  onSubmit: (data: { telegram_user_id: string; reason?: string; duration_days?: number }) => void;
  isSubmitting: boolean;
}

function CreateBanModal({ onClose, onSubmit, isSubmitting }: CreateBanModalProps) {
  const [telegramUserId, setTelegramUserId] = useState('');
  const [reason, setReason] = useState('');
  const [isPermanent, setIsPermanent] = useState(true);
  const [durationDays, setDurationDays] = useState(7);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!telegramUserId.trim()) return;

    onSubmit({
      telegram_user_id: telegramUserId.trim(),
      reason: reason.trim() || undefined,
      duration_days: isPermanent ? undefined : durationDays,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="cyber-card w-full max-w-md">
        {/* Header */}
        <div className="p-4 border-b border-cyber-light">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-display text-white flex items-center gap-2">
              <Ban className="w-4 h-4 text-neon-red" />
              Добавить бан
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-xs text-gray-500 uppercase mb-1 block">
              Telegram User ID <span className="text-neon-red">*</span>
            </label>
            <input
              type="text"
              className="cyber-input text-sm"
              placeholder="123456789"
              value={telegramUserId}
              onChange={(e) => setTelegramUserId(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase mb-1 block">Причина</label>
            <textarea
              className="cyber-input text-sm h-20 resize-none"
              placeholder="Укажите причину бана..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
          </div>

          <div className="space-y-3">
            <label className="text-xs text-gray-500 uppercase block">Срок</label>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="banType"
                  checked={isPermanent}
                  onChange={() => setIsPermanent(true)}
                  className="w-4 h-4 accent-neon-cyan"
                />
                <span className="text-sm text-gray-300">Навсегда</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="banType"
                  checked={!isPermanent}
                  onChange={() => setIsPermanent(false)}
                  className="w-4 h-4 accent-neon-cyan"
                />
                <span className="text-sm text-gray-300">Временный</span>
              </label>
            </div>

            {!isPermanent && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  className="cyber-input text-sm w-20"
                  min={1}
                  max={365}
                  value={durationDays}
                  onChange={(e) => setDurationDays(parseInt(e.target.value, 10) || 1)}
                />
                <span className="text-sm text-gray-400">дней</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="cyber-button text-sm" onClick={onClose}>
              Отмена
            </button>
            <button
              type="submit"
              className="cyber-button-danger flex items-center gap-2 text-sm"
              disabled={isSubmitting || !telegramUserId.trim()}
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : <Ban className="w-3.5 h-3.5" />}
              Заблокировать
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
